import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiUsers, 
  FiTruck, 
  FiTool, 
  FiShoppingBag, 
  FiMonitor,
  FiRadio,
  FiArrowRight,
  FiCheck,
  FiHome,
  FiStar,
  FiClipboard,
  FiBriefcase
} from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import Chatbot from '../components/Chatbot';
import '../styles/pages/Services.css';
import '../styles/animations/ScrollAnimations.css';

// Import service images
import janitorialImg from '../images/MEDC SERVICES PICTURES/Janitorial_Services.png';
import messengersImg from '../images/MEDC SERVICES PICTURES/Messengers_Collectors.png';
import driversImg from '../images/MEDC SERVICES PICTURES/Company_Drivers.png';
import constructionImg from '../images/MEDC SERVICES PICTURES/Construction_Workers.png';
import promotionImg from '../images/MEDC SERVICES PICTURES/Promotion_Merchandisers.png';
import cctvImg from '../images/MEDC SERVICES PICTURES/CCTV_Operators.png';
import radioImg from '../images/MEDC SERVICES PICTURES/Radio_Operators.png';
import carpetImg from '../images/MEDC SERVICES PICTURES/Carpet_Shampooing.png';
import clerksImg from '../images/MEDC SERVICES PICTURES/Office_Temp_Clerks.png';
import cashSortersImg from '../images/MEDC SERVICES PICTURES/Cash_Sorters.png';
import conciergeImg from '../images/MEDC SERVICES PICTURES/Concierge_Services.png';
import otherServicesImg from '../images/MEDC SERVICES PICTURES/Other_Allied_Services.png';

const Services = () => {
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('[class*="scroll-"]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: <FiUsers />,
      number: '01',
      title: 'Janitorial Services',
      image: janitorialImg,
      description: 'Professional cleaning and maintenance services for offices, buildings, and commercial establishments.',
      features: [
        'Daily office cleaning and sanitation',
        'Floor maintenance and polishing',
        'Restroom cleaning and supplies',
        'Waste management and disposal',
        'Window and glass cleaning'
      ]
    },
    {
      icon: <FiClipboard />,
      number: '02',
      title: 'Messengers/Collectors',
      image: messengersImg,
      description: 'Reliable messenger and collection services for documents, payments, and deliveries.',
      features: [
        'Document pickup and delivery',
        'Payment collection services',
        'Bank transactions and deposits',
        'Inter-office document routing',
        'Timely and secure handling'
      ]
    },
    {
      icon: <FiTruck />,
      number: '03',
      title: 'Company Drivers',
      image: driversImg,
      description: 'Professional and licensed drivers for your company vehicles and transportation needs.',
      features: [
        'Licensed professional drivers',
        'Executive and VIP transport',
        'Delivery and logistics support',
        'Vehicle maintenance coordination',
        'Flexible scheduling options'
      ]
    },
    {
      icon: <FiTool />,
      number: '04',
      title: 'Construction Workers',
      image: constructionImg,
      description: 'Skilled construction workers for building projects, renovations, and maintenance work.',
      features: [
        'Skilled laborers and helpers',
        'Carpentry and masonry work',
        'Electrical and plumbing support',
        'Site maintenance and cleanup',
        'Project-based deployment'
      ]
    },
    {
      icon: <FiShoppingBag />,
      number: '05',
      title: 'Promotion Merchandisers',
      image: promotionImg,
      description: 'Trained merchandisers for product promotions, displays, and retail support.',
      features: [
        'Product display and arrangement',
        'Promotional activities support',
        'Inventory monitoring',
        'Customer engagement',
        'Sales support services'
      ]
    },
    {
      icon: <FiMonitor />,
      number: '06',
      title: 'CCTV Operators',
      image: cctvImg,
      description: 'Trained security personnel for CCTV monitoring and surveillance operations.',
      features: [
        '24/7 surveillance monitoring',
        'Incident documentation',
        'Security protocol compliance',
        'Real-time threat assessment',
        'Coordination with security teams'
      ]
    },
    {
      icon: <FiRadio />,
      number: '07',
      title: 'Radio Operators',
      image: radioImg,
      description: 'Professional radio operators for communication and coordination services.',
      features: [
        'Communication management',
        'Dispatch coordination',
        'Emergency response support',
        'Log maintenance and reporting',
        'Multi-channel operations'
      ]
    },
    {
      icon: <FiHome />,
      number: '08',
      title: 'Carpet Shampooing',
      image: carpetImg,
      description: 'Professional carpet and upholstery cleaning services for offices and commercial spaces.',
      features: [
        'Deep carpet cleaning',
        'Stain removal treatment',
        'Upholstery shampooing',
        'Deodorizing services',
        'Quick drying techniques'
      ]
    },
    {
      icon: <FiBriefcase />,
      number: '09',
      title: 'Office Temp Clerks',
      image: clerksImg,
      description: 'Temporary clerical staff for administrative support and office operations.',
      features: [
        'Data entry and filing',
        'Document processing',
        'Reception and front desk',
        'Administrative assistance',
        'Flexible contract terms'
      ]
    },
    {
      icon: <FiClipboard />,
      number: '10',
      title: 'Cash Sorters',
      image: cashSortersImg,
      description: 'Trained personnel for cash handling, sorting, and counting operations.',
      features: [
        'Cash counting and sorting',
        'Currency verification',
        'Banking preparation',
        'Secure handling protocols',
        'Accuracy and accountability'
      ]
    },
    {
      icon: <FiStar />,
      number: '11',
      title: 'Concierge Services',
      image: conciergeImg,
      description: 'Professional concierge staff for buildings, hotels, and corporate establishments.',
      features: [
        'Guest reception and assistance',
        'Information and directions',
        'Reservation coordination',
        'VIP guest handling',
        'Building access management'
      ]
    },
    {
      icon: <FiUsers />,
      number: '12',
      title: 'Other Allied Services',
      image: otherServicesImg,
      description: 'Comprehensive manpower solutions for various business needs and requirements.',
      features: [
        'Customized staffing solutions',
        'Skilled and unskilled labor',
        'Short and long-term contracts',
        'Industry-specific personnel',
        'Flexible deployment options'
      ]
    }
  ];

  const processSteps = [
    {
      number: 1,
      title: 'Contact Us',
      description: 'Reach out to discuss your manpower requirements.'
    },
    {
      number: 2,
      title: 'Assessment',
      description: 'We assess your needs and provide a customized proposal.'
    },
    {
      number: 3,
      title: 'Deployment',
      description: 'Trained personnel are deployed to your location.'
    },
    {
      number: 4,
      title: 'Monitoring',
      description: 'Continuous supervision and quality assurance.'
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <h1>{t('services.title')}</h1>
          <p>
            {t('services.subtitle')}
          </p>
        </div>
      </section>

      {/* Main Services Section */}
      <section className="main-services section">
        <div className="container">
          <div className="services-list">
            {services.map((service, index) => (
              <div 
                className="service-item scroll-fade-up" 
                key={index}
                style={{ transitionDelay: `${index * 0.05}s` }}
              >
                <div className="service-image">
                  <div className="service-image-wrapper">
                    <img src={service.image} alt={service.title} />
                  </div>
                  <div className="service-number">{service.number}</div>
                </div>
                <div className="service-details">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feature, idx) => (
                      <li key={idx}>
                        <span className="check-icon"><FiCheck /></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="btn btn-primary">
                    Inquire Now <FiArrowRight />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section section">
        <div className="container">
          <div className="section-header scroll-fade-up">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              Simple and straightforward process to get the manpower you need
            </p>
          </div>
          <div className="process-steps scroll-scale">
            {processSteps.map((step, index) => (
              <div className="process-step" key={index}>
                <div className="step-number">{step.number}</div>
                <h4>{step.title}</h4>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chatbot */}
      <Chatbot />
    </div>
  );
};

export default Services;

